import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { BarChart3, Download, TrendingUp, Activity } from 'lucide-react';

const URGENCY_COLORS = { emergency: '#ef4444', urgent: '#f97316', routine: '#10b981', follow_up: '#3b82f6' };

export default function AnalyticsPage() {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [days, setDays] = useState('7');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      API.get('/analytics/overview'),
      API.get(`/analytics/trends?days=${days}`)
    ]).then(([ov, tr]) => {
      setOverview(ov.data);
      setTrends(tr.data.trends || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [days]);

  const exportCSV = () => {
    if (!trends.length) return;
    const headers = ['Date', 'Total Sessions', 'Completed'];
    const rows = trends.map(t => [t.date, t.total, t.completed]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meddivo-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pieData = overview?.triage_distribution
    ? Object.entries(overview.triage_distribution).filter(([,v]) => v > 0).map(([name, value]) => ({ name: name.replace('_', ' '), value }))
    : [];

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-8 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="analytics-title" className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Analytics</h1>
          <p className="text-slate-500 mt-1">Triage performance metrics and trends</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger data-testid="analytics-days-select" className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="export-csv-btn" variant="outline" onClick={exportCSV}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Sessions', value: overview?.total_sessions || 0, color: 'sky' },
          { label: 'Completed Today', value: overview?.completed_today || 0, color: 'emerald' },
          { label: 'Emergency Cases', value: overview?.emergency_cases || 0, color: 'red' },
          { label: 'Escalated', value: overview?.escalated || 0, color: 'purple' },
        ].map((s, i) => (
          <div key={i} className="stat-card" data-testid={`analytics-stat-${i}`}>
            <p className="text-sm font-medium text-slate-500">{s.label}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" style={{fontFamily: 'Manrope'}}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-lg" style={{fontFamily: 'Manrope'}}>Session Trends</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[300px]" data-testid="line-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  <Line type="monotone" dataKey="total" stroke="#0ea5e9" strokeWidth={2} dot={{ fill: '#0ea5e9', r: 4 }} name="Total" />
                  <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }} name="Completed" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-lg" style={{fontFamily: 'Manrope'}}>Urgency Distribution</CardTitle></CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <>
                <div className="h-[240px]" data-testid="analytics-pie-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={URGENCY_COLORS[entry.name.replace(' ', '_')] || '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {Object.entries(URGENCY_COLORS).map(([key, color]) => (
                    <div key={key} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-xs text-slate-600 capitalize">{key.replace('_', ' ')}: {overview?.triage_distribution?.[key] || 0}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400"><BarChart3 className="w-8 h-8 opacity-50" /></div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
